<template>
  <div class="payments-wrapper">
    <header class="page-header fade-in">
      <div>
        <h1>Paiements</h1>
        <p>Consultez les paiements enregistrés et leur état.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline" @click="refresh" :disabled="loading">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Actualiser</span>
        </button>
        <button class="btn btn-primary" @click="createPayment">
          <i class="bi bi-cash-stack"></i>
          <span>Nouveau paiement</span>
        </button>
      </div>
    </header>

    <section class="page-body">
      <div class="filters">
        <div class="search">
          <i class="bi bi-search"></i>
          <input
            v-model="searchTerm"
            type="search"
            placeholder="Rechercher (référence, vente...)"
          />
        </div>
        <div class="stats" v-if="filteredPayments.length">
          <span>{{ filteredPayments.length }} paiement(s)</span>
        </div>
      </div>

      <div v-if="loading" class="state">
        <div class="loader"></div>
        <p>Chargement des paiements…</p>
      </div>

      <div v-else-if="!filteredPayments.length" class="state">
        <i class="bi bi-credit-card"></i>
        <h3>Aucun paiement</h3>
        <p>Ajoutez votre premier paiement pour démarrer le suivi.</p>
        <button class="btn btn-primary" @click="createPayment">
          <i class="bi bi-cash-stack"></i>
          <span>Enregistrer un paiement</span>
        </button>
      </div>

      <div v-else class="payments-grid">
        <article
          v-for="payment in filteredPayments"
          :key="payment.reference"
          class="payment-card payment-card--compact fade-in"
        >
          <div class="card-compact">
            <div class="card-info">
              <div class="identity-row">
                <div class="identity-main">
                  <h3>{{ payment.customerName || "—" }}</h3>
                  <p class="identity-sub muted">
                    {{ payment.reference || "—" }}
                  </p>
                </div>
                <div class="identity-actions">
                  <span class="badge" v-if="payment.method">{{
                    payment.method
                  }}</span>
                  <button
                    class="btn btn-ghost btn-ghost--small"
                    @click="
                      viewPayment(payment.paymentReference || payment.reference)
                    "
                  >
                    <i class="bi bi-arrow-up-right"></i>
                  </button>
                </div>
              </div>
              <p
                class="identity-sub muted reference-extra"
                v-if="payment.sellerSaleReference || payment.sellerReference"
              >
                <span v-if="payment.sellerSaleReference">
                  Vente : {{ payment.sellerSaleReference }}
                </span>
                <span
                  v-if="payment.sellerReference"
                  :class="{ divider: payment.sellerSaleReference }"
                >
                  Vendeur : {{ payment.sellerReference }}
                </span>
              </p>
            </div>

            <div class="card-metrics">
              <div class="metric">
                <span class="label"
                  ><i class="bi bi-currency-exchange"></i>Montant</span
                >
                <span class="value">{{ formatCurrency(payment.amount) }}</span>
              </div>
              <div class="metric">
                <span class="label"
                  ><i class="bi bi-calendar-date"></i>Date</span
                >
                <span class="value">{{ formatDate(payment.date) }}</span>
              </div>
            </div>

            <div class="card-actions card-actions--compact">
              <button
                class="btn btn-outline"
                @click="
                  editPayment(payment.paymentReference || payment.reference)
                "
              >
                <i class="bi bi-pencil"></i>
                <span>Modifier</span>
              </button>
              <button
                class="btn btn-danger"
                @click="
                  deletePayment(payment.paymentReference || payment.reference)
                "
              >
                <i class="bi bi-trash"></i>
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </article>
      </div>
    </section>

    <button class="fab" @click="createPayment">
      <i class="bi bi-plus-lg"></i>
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PaymentsList",
  data() {
    return {
      loading: false,
      searchTerm: "",
    };
  },
  computed: {
    ...mapGetters("payment", {
      payments: "getAll",
    }),
    normalizedPayments() {
      return (this.payments || []).map((raw) => {
        const reference =
          raw.reference || raw.payment_reference || raw.id || "—";
        return {
          reference,
          paymentReference: raw.payment_reference || reference,
          method:
            raw.method ||
            raw.payment_method ||
            raw.method_label ||
            raw.paymentType ||
            "",
          amount:
            Number(
              raw.amount != null
                ? raw.amount
                : raw.total_amount != null
                ? raw.total_amount
                : raw.total
            ) || 0,
          date:
            raw.date ||
            raw.payment_date ||
            raw.paymentDate ||
            raw.created_at ||
            raw.createdAt ||
            "",
          customerName:
            raw.customerFullName ||
            raw.customer_full_name ||
            raw.customer?.fullName ||
            raw.customer?.name ||
            raw.customer_name ||
            "",
          sellerSaleReference:
            raw.seller_sale_reference ||
            raw.sale?.reference ||
            raw.sellerSaleReference ||
            "",
          sellerReference:
            raw.seller_reference ||
            raw.seller?.reference ||
            raw.user?.reference ||
            "",
          raw,
        };
      });
    },
    filteredPayments() {
      if (!this.searchTerm) {
        return this.normalizedPayments;
      }
      const term = this.searchTerm.toLowerCase();
      return this.normalizedPayments.filter((payment) => {
        const values = [
          payment.reference,
          payment.paymentReference,
          payment.customerName,
          payment.sellerSaleReference,
          payment.sellerReference,
          payment.method,
        ]
          .filter(Boolean)
          .map((value) => value.toString().toLowerCase());
        return values.some((value) => value.includes(term));
      });
    },
  },
  methods: {
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch (error) {
        return value;
      }
    },
    async fetchPayments() {
      this.loading = true;
      try {
        await this.$store.dispatch("payment/getAll");
      } catch (error) {
        console.error("Error loading payments", error);
        this.$toast.error("Impossible de charger les paiements.");
      } finally {
        this.loading = false;
      }
    },
    refresh() {
      this.fetchPayments();
    },
    createPayment() {
      this.$router.push({ name: "payments-create" });
    },
    viewPayment(reference) {
      this.$router.push({ name: "payments-show", params: { reference } });
    },
    editPayment(reference) {
      this.$router.push({ name: "payments-edit", params: { reference } });
    },
    async deletePayment(reference) {
      if (!reference) return;
      const confirmed = await this.$confirm({
        title: "Supprimer le paiement",
        message: "Êtes-vous sûr de vouloir supprimer ce paiement ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("payment/destroy", reference);
        this.$toast.success("Paiement supprimé avec succès.");
      } catch (error) {
        console.error("Error deleting payment", error);
        this.$toast.error("Impossible de supprimer le paiement.");
      }
    },
  },
  mounted() {
    if (!this.payments || this.payments.length === 0) {
      this.fetchPayments();
    }
  },
};
</script>

<style scoped>
.payments-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.5rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
  max-width: 480px;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
}

.search {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1.2rem;
  border-radius: 16px;
  background: #f3f4f8;
  border: 1px solid #e4e7f1;
  min-width: 280px;
  flex: 1;
}

.search input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 1rem;
  width: 100%;
}

.stats {
  color: #6b7280;
  font-weight: 600;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: #6b7280;
  text-align: center;
}

.state i {
  font-size: 3rem;
  color: #2563eb;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.payments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.payment-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), #ffffff 100%);
  border-radius: 18px;
  padding: 1rem 1.5rem;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.payment-card--compact {
  padding: 1rem 1.25rem;
}

.payment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 38px rgba(15, 23, 42, 0.12);
}

.payment-card--compact .card-compact {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr) auto;
  align-items: center;
  gap: 1rem;
}

.card-info {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.identity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.identity-main h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #0f172a;
}

.identity-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.badge {
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.identity-sub {
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
}

.identity-sub.muted {
  color: #94a3b8;
  font-size: 0.9rem;
}
.identity-sub.muted .divider {
  margin-left: 0.75rem;
  padding-left: 0.75rem;
  border-left: 1px solid rgba(148, 163, 184, 0.4);
}
.reference-extra {
  margin-top: 0.15rem;
}

.btn.btn-ghost {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: rgba(37, 99, 235, 0.08);
  color: #1d4ed8;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.btn.btn-ghost:hover {
  background: rgba(37, 99, 235, 0.2);
}

.btn-ghost--small {
  width: 36px;
  height: 36px;
  font-size: 1rem;
}

.card-metrics {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-start;
}

.metric {
  background: #f8faff;
  border-radius: 14px;
  padding: 0.65rem 0.95rem;
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.card-actions--compact {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.label i {
  color: #2563eb;
}

.value {
  color: #0f172a;
  font-weight: 600;
  word-break: break-word;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn i {
  font-size: 1.1rem;
}

.btn:hover {
  transform: translateY(-2px);
}

.card-actions--compact .btn {
  padding: 0.55rem 0.95rem;
  border-radius: 12px;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.fab {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #9333ea);
  color: white;
  border: none;
  font-size: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 30px rgba(79, 70, 229, 0.35);
}

@media (max-width: 1024px) {
  .payments-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
  .page-header {
    flex-direction: column;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .payment-card--compact .card-compact {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  .card-metrics {
    flex-wrap: wrap;
  }
  .card-actions--compact {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .payments-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .search {
    min-width: unset;
  }
  .payments-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .page-body {
    padding: 1.25rem;
  }
  .payment-card {
    padding: 1.25rem;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
  .actions {
    flex-direction: column;
  }
}
</style>
